import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { Tree, Input, message } from 'antd'

const { Search } = Input
interface TreeNodeData {
  userId: {
    _id: string
    id: string
    name: string
    fullName: string
    email: string
    refCode: string
    avatar: string
    level: number
  }
  parentUserId?: any
  _id: string
  id: string
  name: string
  url: string
  level: number
  email: string
  totalF1: number
  totalNetVolume: number
  totalNetVolumeMBC: number
  totalNodeVolume: number
  totalNodeVolumeMBC: number
  totalNodeBalance: number
  totalNodeBalanceMBC: number
  childsPairBalance: number
  childsPairBalanceMBC: number
  totalMBCNetBought: number
  totalUSDTNetBought: number
  children?: TreeNodeData[]
}

interface ExtendedHierarchyPointNode<T> extends d3.HierarchyPointNode<T> {
  x0: number
  y0: number
  _children?: d3.HierarchyNode<T>[] | null
}

const hexagonPath = (size: number) => {
  const r = size
  return `
    M 0 ${-r} 
    L ${r * Math.sin(Math.PI / 3)} ${-r / 2} 
    L ${r * Math.sin(Math.PI / 3)} ${r / 2} 
    L 0 ${r} 
    L ${-r * Math.sin(Math.PI / 3)} ${r / 2} 
    L ${-r * Math.sin(Math.PI / 3)} ${-r / 2} 
    Z
  `
}

interface TreeComponentProps {
  data: any
}

const TreeComponent: React.FC<TreeComponentProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [copySuccess, setCopySuccess] = useState<string | null>(null)
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null)
  const [tooltip, setTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    content: string
  }>({ visible: false, x: 0, y: 0, content: '' })

  // const svgRef = useRef(null) // <svg ref={svgRef}>
  const gRef = useRef(null) // <g ref={gRef}> để chứa toàn bộ cây
  const zoomRef = useRef<any>(null) // Lưu d3.zoom object

  useEffect(() => {
    if (!svgRef.current || !data) return

    const width = window.innerWidth
    const height = window.innerHeight
    const dx = 120 // khoảng cách dọc (giữa các cấp)
    const dy = 180 // khoảng cách ngang (giữa các node cùng cấp)
    const hexSize = 30

    const root = d3.hierarchy<TreeNodeData>(data)
    const treeLayout = d3.tree<TreeNodeData>().nodeSize([dx, dy])
    treeLayout(root)

    const rootPoint = root as ExtendedHierarchyPointNode<TreeNodeData>
    rootPoint.x0 = rootPoint.x || 0
    rootPoint.y0 = rootPoint.y || 0
    rootPoint.descendants().forEach((d) => {
      const nd = d as ExtendedHierarchyPointNode<TreeNodeData>
      nd.x0 = nd.x || 0
      nd.y0 = nd.y || 0
      nd._children = nd.children
    })

    // Tính bounds của cây để căn giữa
    const allNodes = rootPoint.descendants()
    const minX = d3.min(allNodes, (d) => d.x) || 0
    const maxX = d3.max(allNodes, (d) => d.x) || 0
    const minY = d3.min(allNodes, (d) => d.y) || 0
    const maxY = d3.max(allNodes, (d) => d.y) || 0

    const treeWidth = maxX - minX
    const treeHeight = maxY - minY

    // Căn giữa cây trong khung SVG
    const initialX = width / 3.3
    const initialY = height / 4

    let currentTransform = d3.zoomIdentity.translate(initialX, initialY)

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    const g = svg.append('g')
    const gLink = g.append('g').attr('fill', 'none').attr('stroke', '#999').attr('stroke-width', 1.2)
    const gNode = g.append('g').attr('cursor', 'pointer')

    // let currentTransform = d3.zoomIdentity

    // Zoom + set transform
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.001, 3])
      .on('zoom', (event) => {
        currentTransform = event.transform
        g.attr('transform', currentTransform.toString())
      })

    svg.call(zoom).call(zoom.transform, currentTransform)

    function update(source: ExtendedHierarchyPointNode<TreeNodeData>) {
      const duration = 750
      treeLayout(rootPoint)

      const nodes = rootPoint.descendants() as ExtendedHierarchyPointNode<TreeNodeData>[]
      const links = rootPoint.links()

      svg.attr('width', width).attr('height', height)

      const node = gNode
        .selectAll<SVGGElement, ExtendedHierarchyPointNode<TreeNodeData>>('g')
        .data(nodes, (d) => d.data._id)

      const nodeEnter = node
        .enter()
        .append('g')
        .attr('transform', () => `translate(${source.x0},${source.y0})`)
        .on('click', (event, d: any) => {
          event.stopPropagation()
          /* eslint-disable no-param-reassign */
          if (d.children) {
            d._children = d.children
            d.children = null
          } else {
            d.children = d._children
            d._children = null
          }
          /* eslint-enable no-param-reassign */

          update(d)
        })
        .on('mouseenter', (event, d) => {
          const content =
            `UID: ${d.data.id}\n` +
            `Email: ${d.data.email}\n` +
            `Level: ${d.data.level}\n` +
            `Net Volume: ${d.data.totalNetVolume} USDT\n` +
            `Net Volume MBC: ${d.data.totalNetVolumeMBC} MBC\n` +
            `Package: ${d.data.totalNodeVolume} USDT\n` +
            `Package MBC: ${d.data.totalNodeVolumeMBC} MBC\n` +
            `Parent pair Balance: ${d?.data?.totalNodeBalance || 0} USDT\n` +
            `Parent pair MBC Balance: ${d?.data?.totalNodeBalanceMBC || 0} USDT (MBC)\n` +
            `F1 pair Balance: ${d?.data?.childsPairBalance || 0} USDT\n` +
            `F1 pair MBC Balance: ${d?.data?.childsPairBalanceMBC || 0} USDT (MBC)`
          setTooltip({ visible: true, x: event.clientX, y: event.clientY, content })
        })
        .on('mouseleave', () => {
          setTooltip((t) => ({ ...t, visible: false }))
        })

      nodeEnter
        .append('path')
        .attr('d', hexagonPath(hexSize))
        .attr('fill', '#cce5ff')
        .attr('stroke', '#2a73cc')
        .attr('stroke-width', 2)

      nodeEnter
        .append('path')
        .attr('d', hexagonPath(hexSize))
        .attr('stroke', (d) => (d.data.id === highlightedNodeId ? '#00ff66' : ''))
        .attr('stroke-width', (d) => (d.data.id === highlightedNodeId ? 20 : 0))

      nodeEnter
        .append('image')
        .attr('href', (d) => d.data.userId?.avatar || 'https://cdn-icons-png.flaticon.com/512/149/149071.png')
        .attr('x', -hexSize)
        .attr('y', -hexSize)
        .attr('width', hexSize * 2)
        .attr('height', hexSize * 2)
        .attr('clip-path', `url(#hexClip)`)

      svg.select('defs').remove()
      svg.append('defs').append('clipPath').attr('id', 'hexClip').append('path').attr('d', hexagonPath(hexSize))

      nodeEnter
        .append('text')
        .attr('dy', hexSize + 15)
        .attr('text-anchor', 'middle')
        .text((d) => d.data.name || d.data.userId?.name || 'No Name')

      nodeEnter
        .append('text')
        .attr('class', 'copy-icon')
        .attr('font-family', 'FontAwesome, Arial')
        .attr('font-size', 30)
        .attr('cursor', 'pointer')
        .attr('x', hexSize * 0.7)
        .attr('y', hexSize * 0.7)
        .text('⧉')
        .on('click', (event, d) => {
          event.stopPropagation()
          const content =
            `UID: ${d.data.id}\n` +
            `Email: ${d.data.email}\n` +
            `Level: ${d.data.level}\n` +
            `Net Volume: ${d.data.totalNetVolume} USDT\n` +
            `Net Volume MBC: ${d.data.totalNetVolumeMBC} MBC\n` +
            `Package: ${d.data.totalNodeVolume} USDT\n` +
            `Package MBC: ${d.data.totalNodeVolumeMBC} MBC\n` +
            `Parent pair Balance: ${d?.data?.totalNodeBalance || 0} USDT\n` +
            `Parent pair MBC Balance: ${d?.data?.totalNodeBalanceMBC || 0} USDT (MBC)\n` +
            `F1 pair Balance: ${d?.data?.childsPairBalance || 0} USDT\n` +
            `F1 pair MBC Balance: ${d?.data?.childsPairBalanceMBC || 0} USDT (MBC)`
          navigator.clipboard.writeText(content).then(() => {
            setCopySuccess(`Copied node ${d.data.name} data!`)
            setTimeout(() => setCopySuccess(null), 1500)
            setTooltip({ visible: true, x: event.clientX, y: event.clientY, content })
          })
        })
      nodeEnter
        .append('circle')
        .attr('cx', 0)
        .attr('cy', -hexSize - 6)
        .attr('r', 5)
        .attr('fill', (d) => (d.children || d._children ? '#4caf50' : '#999'))

      const nodeUpdate = node
        .merge(nodeEnter)
        .transition()
        .duration(duration)
        .attr('transform', (d) => `translate(${d.x},${d.y})`)

      nodeUpdate.select('path').attr('fill', (d) => (d._children ? '#a0c4ff' : '#cce5ff'))

      const nodeExit = node
        .exit()
        .transition()
        .duration(duration)
        .attr('transform', () => `translate(${source.x},${source.y})`)
        .remove()

      nodeExit.select('path').attr('r', 0)
      nodeExit.select('text').style('fill-opacity', 0)

      const link = gLink
        .selectAll<SVGPathElement, d3.HierarchyLink<TreeNodeData>>('path')
        .data(links, (d) => d.target.data._id)

      const linkEnter = link
        .enter()
        .append('path')
        .attr('d', () => {
          const o: [number, number] = [source.x0, source.y0]
          return d3
            .linkVertical()
            .x((d) => d[0])
            .y((d) => d[1])({ source: o, target: o })
        })

      link
        .merge(linkEnter)
        .transition()
        .duration(duration)
        .attr(
          'd',
          d3
            .linkVertical()
            .x((d: any) => d.x)
            .y((d: any) => d.y) as any,
        )

      link
        .exit()
        .transition()
        .duration(duration)
        .attr('d', () => {
          const o: [number, number] = [source.x, source.y]
          return d3
            .linkVertical()
            .x((d) => d[0])
            .y((d) => d[1])({ source: o, target: o })
        })
        .remove()

      rootPoint.eachBefore((d) => {
        const nd = d as ExtendedHierarchyPointNode<TreeNodeData>
        nd.x0 = nd.x
        nd.y0 = nd.y
      })
    }

    update(rootPoint)
  }, [data, highlightedNodeId])

  function findNodeInTree(tree: TreeNodeData, query: string): TreeNodeData | null {
    if (tree.email === query || tree.id === query) return tree
    if (!tree.children) return null

    let result: TreeNodeData | null = null

    tree.children.forEach((child) => {
      if (result) return // đã tìm thấy, không đệ quy tiếp

      const found = findNodeInTree(child, query)
      if (found) {
        result = found
      }
    })

    return result
  }

  const scrollToNode = (nodeId: string) => {
    console.log(`nodeId : ${nodeId}`)
    const nodeElement = d3.select(gRef.current).select(`#node-${nodeId}`)
    if (nodeElement.empty()) return

    // Lấy tọa độ của node (assumes bạn gán transform trong D3)
    const transform = nodeElement.attr('transform') // ví dụ: "translate(100,200)"
    console.log(`transform`)
    console.log(transform)
    const match = transform.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/)
    if (!match) return
    const [, x, y] = match.map(Number)

    const svg = d3.select(svgRef.current)
    const svgNode = svg.node() as any
    const width = svgNode.clientWidth
    const height = svgNode.clientHeight

    // Zoom vào node, căn giữa
    const scale = 10 // zoom nhẹ vào
    console.log(`scale : ${scale}`)
    const translateX = width / 2 - x * scale
    const translateY = height / 2 - y * scale

    svg
      .transition()
      .duration(750)
      .call(zoomRef.current.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale))
  }

  const handleSearch = (value: string) => {
    if (!value) return

    // Tìm node theo email hoặc UID trong dữ liệu gốc của cây
    const foundNode = findNodeInTree(data, value) // tự viết hàm này
    if (foundNode) {
      setHighlightedNodeId(foundNode.id)
      // Có thể scroll vào node hoặc zoom vào node
      // scrollToNode(foundNode.id) // hoặc d3 zoom nếu dùng D3
    } else {
      message.warning('Không tìm thấy người dùng.')
      setHighlightedNodeId(null)
    }
  }

  useEffect(() => {
    if (highlightedNodeId) {
      requestAnimationFrame(() => scrollToNode(highlightedNodeId))
    }
  }, [highlightedNodeId])

  return (
    <>
      <Search placeholder="Nhập UID hoặc Email" allowClear onSearch={handleSearch} style={{ marginBottom: 12 }} />
      <svg ref={svgRef} style={{ width: '100%', height: '100vh', border: '1px solid #ccc', background: '#fafafa' }} />
      {tooltip.visible && (
        <pre
          style={{
            position: 'fixed',
            top: tooltip.y + 10,
            left: tooltip.x + 10,
            background: '#333',
            color: '#fff',
            padding: '8px',
            borderRadius: '4px',
            whiteSpace: 'pre-line',
            pointerEvents: 'none',
            fontSize: 12,
            zIndex: 9999,
            maxWidth: 220,
          }}
        >
          {tooltip.content}
        </pre>
      )}
      {copySuccess && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#4caf50',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            fontWeight: 'bold',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {copySuccess}
        </div>
      )}
    </>
  )
}

export default TreeComponent
